import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import "../styles/projLoginPage.css";
import { API_BASE } from "../config";
import ServerCaptchaWidget from "./ServerCaptchaWidget";
import SECURITY_QUESTIONS from "../config/securityQuestions.json";
import apiFetch from "../apiFetch";

export default function ForgotPassword() {
  const [step, setStep] = useState("username"); // username | email-sent | security-q | new-password | done | no-recovery
  const [username, setUsername] = useState("");
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Bot protection state
  const [honeypot, setHoneypot] = useState("");
  const [formLoadedAt, setFormLoadedAt] = useState(null);
  const [stepLoadedAt, setStepLoadedAt] = useState(null);
  const [captchaOk, setCaptchaOk] = useState(false);
  const captchaRef = useRef(null);

  useEffect(() => { setFormLoadedAt(Date.now()); }, []);

  // Step 1: submit username
  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return setError("Please enter your username.");
    if (!captchaOk) return setError("Please complete the security check.");
    setError("");
    setIsLoading(true);

    const { token: captchaToken, answer: captchaAnswer } = captchaRef.current?.getChallenge() ?? {};

    try {
      const res = await apiFetch(`/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          captchaToken,
          captchaAnswer,
          website: honeypot,
          formLoadedAt,
        }),
      });
      const data = await res.json();

      if (!data.success) {
        setError(data.message || "Something went wrong.");
        captchaRef.current?.refresh();
        setCaptchaOk(false);
        return;
      }

      if (data.method === "email") {
        setStep("email-sent");
      } else if (data.method === "security_question") {
        setSecurityQuestion(data.securityQuestion);
        setStepLoadedAt(Date.now());
        setStep("security-q");
      } else {
        setStep("no-recovery");
      }
    } catch {
      setError("Something went wrong. Please try again.");
      captchaRef.current?.refresh();
      setCaptchaOk(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2b: verify security answer
  const handleAnswerSubmit = async (e) => {
    e.preventDefault();
    if (!answer.trim()) return setError("Please enter your answer.");
    setError("");
    setIsLoading(true);

    try {
      const res = await apiFetch(`/api/auth/verify-security-answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          answer: answer.trim(),
          website: honeypot,
          stepLoadedAt,
        }),
      });
      const data = await res.json();

      if (data.success) {
        setResetToken(data.token);
        setStep("new-password");
      } else {
        setError(data.message || "Incorrect answer.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: set new password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return setError("Password must be at least 8 characters.");
    if (newPassword !== confirmPassword) return setError("Passwords do not match.");
    setError("");
    setIsLoading(true);

    try {
      const res = await apiFetch(`/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, newPassword }),
      });
      const data = await res.json();

      if (data.success) {
        setStep("done");
      } else {
        setError(data.message || "Failed to reset password.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case "username":
        return (
          <>
            <div className="lp-brand">
              <div className="lp-logo">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h1 className="lp-title">Forgot Password</h1>
              <p className="lp-subtitle">Enter your username to get started</p>
            </div>

            {/* Honeypot */}
            <div className="contact-hp-field" aria-hidden="true">
              <label htmlFor="fp-website">Website</label>
              <input id="fp-website" type="text" value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
            </div>

            <form className="lp-form" onSubmit={handleUsernameSubmit} noValidate>
              {error && <div className="lp-error-banner">{error}</div>}
              <div className="lp-field">
                <label htmlFor="fp-username">Username</label>
                <div className="lp-input-wrap">
                  <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <input id="fp-username" type="text" placeholder="Enter your username"
                    value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
                </div>
              </div>
              <div className="lp-field">
                <ServerCaptchaWidget ref={captchaRef} onVerify={(ok) => setCaptchaOk(ok)} />
              </div>
              <button type="submit" className="lp-submit" disabled={isLoading || !captchaOk}>
                {isLoading ? "Checking…" : "Continue"}
              </button>
            </form>
            <p className="fp-back-link">
              <Link to="/projLoginPage">Back to Sign In</Link>
            </p>
          </>
        );

      case "email-sent":
        return (
          <div className="fp-status">
            <div className="fp-status-icon fp-icon-info">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="lp-title">Check your email</h2>
            <p className="lp-subtitle" style={{ textAlign: "center", maxWidth: 300 }}>
              We sent a password reset link to the email address on your account. It expires in 1 hour.
            </p>
            <p className="fp-back-link" style={{ marginTop: "1.5rem" }}>
              <Link to="/projLoginPage">Back to Sign In</Link>
            </p>
          </div>
        );

      case "security-q":
        return (
          <>
            <div className="lp-brand">
              <div className="lp-logo">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="lp-title">Security Question</h1>
              <p className="lp-subtitle">Answer to reset your password</p>
            </div>
            <form className="lp-form" onSubmit={handleAnswerSubmit} noValidate>
              {error && <div className="lp-error-banner">{error}</div>}
              <div className="lp-field">
                <label>{securityQuestion}</label>
                <div className="lp-input-wrap">
                  <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <input type="text" placeholder="Your answer"
                    value={answer} onChange={(e) => setAnswer(e.target.value)} autoComplete="off" />
                </div>
              </div>
              <button type="submit" className="lp-submit" disabled={isLoading}>
                {isLoading ? "Verifying…" : "Verify Answer"}
              </button>
            </form>
            <p className="fp-back-link">
              <Link to="/projLoginPage">Back to Sign In</Link>
            </p>
          </>
        );

      case "new-password":
        return (
          <>
            <div className="lp-brand">
              <div className="lp-logo">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h1 className="lp-title">New Password</h1>
              <p className="lp-subtitle">Choose a strong password</p>
            </div>
            <form className="lp-form" onSubmit={handlePasswordSubmit} noValidate>
              {error && <div className="lp-error-banner">{error}</div>}
              <div className="lp-field">
                <label htmlFor="fp-new-pw">New password</label>
                <div className="lp-input-wrap">
                  <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <input id="fp-new-pw" type={showPassword ? "text" : "password"}
                    placeholder="At least 8 characters"
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password" />
                  <button type="button" className="lp-eye-btn"
                    onClick={() => setShowPassword((v) => !v)} tabIndex={-1}>
                    {showPassword
                      ? <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                      : <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    }
                  </button>
                </div>
              </div>
              <div className="lp-field">
                <label htmlFor="fp-confirm-pw">Confirm password</label>
                <div className="lp-input-wrap">
                  <svg className="lp-field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <input id="fp-confirm-pw" type={showPassword ? "text" : "password"}
                    placeholder="Repeat password"
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password" />
                </div>
              </div>
              <button type="submit" className="lp-submit" disabled={isLoading}>
                {isLoading ? "Saving…" : "Reset Password"}
              </button>
            </form>
          </>
        );

      case "done":
        return (
          <div className="fp-status">
            <div className="fp-status-icon fp-icon-success">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="lp-title">Password Reset!</h2>
            <p className="lp-subtitle" style={{ textAlign: "center" }}>
              Your password has been updated. You can now sign in.
            </p>
            <p className="fp-back-link" style={{ marginTop: "1.5rem" }}>
              <Link to="/projLoginPage">Sign In</Link>
            </p>
          </div>
        );

      case "no-recovery":
      default:
        return (
          <div className="fp-status">
            <div className="fp-status-icon fp-icon-warn">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h2 className="lp-title">Can't Reset</h2>
            <p className="lp-subtitle" style={{ textAlign: "center", maxWidth: 300 }}>
              No recovery method is set up for this account. Please contact support for help.
            </p>
            <p className="fp-back-link" style={{ marginTop: "1.5rem" }}>
              <Link to="/projLoginPage">Back to Sign In</Link>
            </p>
          </div>
        );
    }
  };

  return (
    <div className="lp-container">
      <div className="lp-card">{renderStep()}</div>
    </div>
  );
}

